import React, { useState } from "react";
import { Question, Option, ArchetypeScore } from "../types";
import QuestionCard from "../components/QuestionCard";
import ProgressBar from "../components/ProgressBar";
import Button from "../components/Button";
import Logo from "../components/Logo";
import { updateScores } from "../utils/scoringUtils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import FeedbackToast from "../components/FeedbackToast";
import { useErrorHandling } from "../hooks/useErrorHandling";
import { useTestValidation } from "../hooks/useTestValidation";
import { trackTestProgress } from "../utils/analytics";

interface QuestionnairePageProps {
  questions: Question[];
  onComplete: (
    scores: ArchetypeScore,
    selectedOptions: Option[],
    categoryScores: any
  ) => void;
  initialScores: ArchetypeScore;
  isLoading?: boolean;
  error?: string | null;
}

const QuestionnairePage: React.FC<QuestionnairePageProps> = ({
  questions,
  onComplete,
  initialScores,
  isLoading = false,
  error = null,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOptionText, setSelectedOptionText] = useState<string | null>(
    null
  );

  // Initialize scores and answers
  type Archetype =
    | "buho"
    | "colibri"
    | "condor"
    | "coqui"
    | "jaguar"
    | "llama"
    | "quetzal";
  type CategoryScores = Record<string, Record<Archetype, number>>;

  const [categoryScores, setCategoryScores] = useState<CategoryScores>({});

  const [scores, setScores] = useState<ArchetypeScore>(initialScores);
  const [answers, setAnswers] = useState<Option[]>(
    new Array(questions.length).fill(null)
  );

  const { error: localError, handleError, clearError } = useErrorHandling();
  const { validateAndSanitizeScores } = useTestValidation();

  const currentQuestion = questions[currentQuestionIndex];

  const handleSelectOption = (option: Option) => {
    if (isLoading) return;
    try {
      setSelectedOptionText(option.text);
      clearError();
    } catch (err) {
      handleError(err, "Select Option");
    }
  };

  const handleNext = async () => {
    if (selectedOptionText === null || isLoading) return;
    try {
      const selectedOption = currentQuestion.options.find(
        (o) => o.text === selectedOptionText
      );
      if (!selectedOption) {
        throw new Error("No option selected");
      }

      //    console.log("currentQuestion", currentQuestion);
      //    console.log("selectedOption", selectedOption);
      // Ensure the category exists in categoryScores

      if (!categoryScores[currentQuestion.category]) {
        categoryScores[currentQuestion.category] = {
          buho: 0,
          colibri: 0,
          condor: 0,
          coqui: 0,
          jaguar: 0,
          llama: 0,
          quetzal: 0,
        };
      }

      categoryScores[currentQuestion.category].buho +=
        selectedOption.scores.buho;
      categoryScores[currentQuestion.category].colibri +=
        selectedOption.scores.colibri;
      categoryScores[currentQuestion.category].condor +=
        selectedOption.scores.condor;
      categoryScores[currentQuestion.category].coqui +=
        selectedOption.scores.coqui;
      categoryScores[currentQuestion.category].jaguar +=
        selectedOption.scores.jaguar;
      categoryScores[currentQuestion.category].llama +=
        selectedOption.scores.llama;
      categoryScores[currentQuestion.category].quetzal +=
        selectedOption.scores.quetzal;

      // Update scores with the new category scores
      setCategoryScores({ ...categoryScores });
      //      console.log("Updated categoryScores:", categoryScores);

      // Update and validate scores
      const newScores = validateAndSanitizeScores(
        updateScores(scores, selectedOption)
      );
      setScores(newScores);

      // Track progress
      await trackTestProgress(currentQuestionIndex + 1, questions.length);

      // Update answers array
      const newAnswers = [...answers];
      newAnswers[currentQuestionIndex] = selectedOption;
      setAnswers(newAnswers);

      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        // Check if there's a previously selected answer for the next question
        const nextAnswer = newAnswers[currentQuestionIndex + 1];
        setSelectedOptionText(nextAnswer ? nextAnswer.text : null);
      } else {
        // Validate all answers before completing
        const hasAllAnswers = newAnswers.every((answer) => answer !== null);
        if (!hasAllAnswers) {
          throw new Error("Please answer all questions before submitting.");
        }
        // console.log("Questionnair Final Category Scores:", categoryScores);
        onComplete(newScores, newAnswers, categoryScores);
      }
      clearError();
    } catch (err) {
      handleError(err, "Next Question");
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0 && !isLoading) {
      try {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
        const previousAnswer = answers[currentQuestionIndex - 1];
        setSelectedOptionText(previousAnswer ? previousAnswer.text : null);
        clearError();
      } catch (err) {
        handleError(err, "Previous Question");
      }
    }
  };

  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  return (
    <>
      <div className="min-h-screen bg-orange-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="flex justify-center mb-8">
            <Logo size="medium" />
          </header>

          <div className="mb-8">
            <ProgressBar
              current={currentQuestionIndex + 1}
              total={questions.length}
            />
          </div>

          <div className="mb-8 transition-opacity duration-300">
            <QuestionCard
              question={currentQuestion}
              onSelectOption={handleSelectOption}
              selectedOptionText={selectedOptionText}
              disabled={isLoading}
            />
          </div>

          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0 || isLoading}
              className="flex items-center"
            >
              <ChevronLeft className="mr-2 h-4 w-4" /> Anterior
            </Button>

            <Button
              onClick={handleNext}
              disabled={selectedOptionText === null || isLoading}
              className="flex items-center"
            >
              {isLoading
                ? "Procesando..."
                : isLastQuestion
                ? "Finalizar Test"
                : "Siguiente"}
              {!isLastQuestion && !isLoading && (
                <ChevronRight className="ml-2 h-4 w-4" />
              )}
            </Button>
          </div>

          {(error || localError) && (
            <FeedbackToast
              message={error || localError || ""}
              type="error"
              onClose={clearError}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default QuestionnairePage;
